import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Check, CreditCard, User, Crown, Loader2, Ticket, X, Tv, Trash2 } from 'lucide-react';
import { CancelSurveyModal } from '../components/CancelSurveyModal';
import { MarketingPreferences } from '../components/MarketingPreferences';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TvDevicesSection() {
  const [devices, setDevices] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/tv/devices`, { withCredentials: true }).then(r => setDevices(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const disconnect = async (id) => {
    if (!window.confirm('Déconnecter cet appareil ? Il devra être reconnecté avec un nouveau code.')) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/tv/devices/${id}`, { withCredentials: true });
      setDevices(d => d.filter(x => x.id !== id));
    } catch { /* ignore */ }
    setBusyId(null);
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  if (devices.length === 0) return null;

  return (
    <div className="bg-[#1a0e2e] rounded-xl p-6 border border-white/5 mb-8" data-testid="tv-devices-section">
      <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
        <Tv size={18} className="text-[#E9641A]" />
        Appareils connectés
      </h2>
      <div className="space-y-3">
        {devices.map(d => (
          <div key={d.id} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-4 py-3" data-testid={`tv-device-${d.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-[#E9641A]/15 flex items-center justify-center flex-none">
                <Tv size={18} className="text-[#E9641A]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{d.name || 'Téléviseur'}</p>
                <p className="text-xs text-white/40">Connecté le {fmt(d.created_at)}{d.last_seen ? ` · Dernière activité : ${fmt(d.last_seen)}` : ''}</p>
              </div>
            </div>
            <button
              onClick={() => disconnect(d.id)}
              disabled={busyId === d.id}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg transition-colors flex-none disabled:opacity-50"
              data-testid={`disconnect-device-${d.id}`}>
              {busyId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              Déconnecter
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AccountPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoValid, setPromoValid] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [subInfo, setSubInfo] = useState(null);
  const [subBusy, setSubBusy] = useState(false);

  const loadSub = useCallback(() => {
    axios.get(`${API}/subscribe/me`, { withCredentials: true }).then(r => setSubInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/plans`).then(r => { setPlans((r.data || []).filter(p => p.price > 0)); setLoading(false); }).catch(() => setLoading(false));
    loadSub();
  }, [loadSub]);

  // Poll for payment status if returning from Stripe
  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 8) { setPaymentStatus('timeout'); setPolling(false); return; }
    try {
      const { data } = await axios.get(`${API}/checkout/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPolling(false);
        // Refresh plans
        axios.get(`${API}/plans`).then(r => setPlans(r.data));
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setPolling(false);
      } else {
        setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && !polling) {
      setPolling(true);
      setPaymentStatus('checking');
      pollStatus(sessionId);
    }
  }, [searchParams, pollStatus, polling]);

  const validatePromo = async (planId) => {
    if (!promoCode.trim()) { setPromoValid(null); setPromoError(''); return; }
    try {
      const { data } = await axios.post(`${API}/promo-codes/validate`, { code: promoCode.trim(), plan_id: planId }, { withCredentials: true });
      setPromoValid(data);
      setPromoError('');
    } catch (err) {
      setPromoValid(null);
      setPromoError(err.response?.data?.detail || 'Code invalide');
    }
  };

  const hasActiveSub = subInfo && ['active', 'trialing'].includes(subInfo.status) && (subInfo.has_stripe_subscription || subInfo.is_test_account);

  const handleSubscribe = async (planId) => {
    setCheckoutLoading(planId);
    try {
      if (hasActiveSub) {
        const msg = subInfo.is_test_account
          ? 'Changer de forfait ? (Compte test : changement immédiat, sans paiement)'
          : 'Changer votre abonnement pour ce forfait ? La différence sera calculée au prorata par Stripe.';
        if (!window.confirm(msg)) return;
        await axios.post(`${API}/subscribe/change`, { plan_id: planId }, { withCredentials: true });
        setPaymentStatus('changed');
        await checkAuth();
        loadSub();
      } else {
        const body = { plan_id: planId, origin_url: window.location.origin };
        if (promoCode.trim()) body.promo_code = promoCode.trim();
        const { data } = await axios.post(`${API}/subscribe/checkout`, body, { withCredentials: true });
        if (data.url) window.location.href = data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const [showCancelSurvey, setShowCancelSurvey] = useState(false);
  const [showWinback, setShowWinback] = useState(() => new URLSearchParams(window.location.search).get('retention') === '1');
  const closeWinback = () => { setShowWinback(false); navigate('/account', { replace: true }); };

  const handleCancelSub = () => setShowCancelSurvey(true);

  const confirmCancelSub = async (reason, comment) => {
    setSubBusy(true);
    try {
      await axios.post(`${API}/subscribe/cancel`, { reason, comment }, { withCredentials: true });
      setShowCancelSurvey(false);
      loadSub();
    } catch (err) {
      alert(err.response?.data?.detail || 'Résiliation impossible');
    } finally {
      setSubBusy(false);
    }
  };

  const handleReactivate = async () => {
    setSubBusy(true);
    try {
      await axios.post(`${API}/subscribe/reactivate`, {}, { withCredentials: true });
      loadSub();
    } catch (err) {
      alert(err.response?.data?.detail || 'Reprise impossible');
    } finally {
      setSubBusy(false);
    }
  };

  const formatPrice = (p) => {
    if (p.price === 0) return 'Gratuit';
    if (p.billing === 'annual') return `${p.price}\u20AC/an`;
    return `${p.price}\u20AC/mois`;
  };

  const currentPlan = user?.subscription_plan || 'free';

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #11041F 0%, #1a0e2e 100%)' }}>
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-white/5">
        <button onClick={() => navigate('/')} className="text-white/40 hover:text-white transition-colors" data-testid="account-back">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-lg font-semibold text-white">Mon compte</h1>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {showCancelSurvey && <CancelSurveyModal busy={subBusy} onConfirm={confirmCancelSub} onClose={() => setShowCancelSurvey(false)} onSaved={loadSub} />}
        {showWinback && <CancelSurveyModal winback busy={false} onConfirm={() => {}} onClose={closeWinback} onSaved={loadSub} />}
        {/* Payment status banner */}
        {paymentStatus === 'checking' && (
          <div className="flex items-center gap-3 bg-[#E9641A]/10 border border-[#E9641A]/30 rounded-xl px-5 py-3 mb-6">
            <Loader2 size={18} className="text-[#E9641A] animate-spin" />
            <p className="text-sm text-white">Vérification du paiement en cours...</p>
          </div>
        )}
        {paymentStatus === 'success' && (
          <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-xl px-5 py-3 mb-6" data-testid="payment-success">
            <Check size={18} className="text-green-400" />
            <p className="text-sm text-white">Paiement réussi ! Votre abonnement a été mis à jour.</p>
          </div>
        )}
        {paymentStatus === 'expired' && (
          <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-3 mb-6">
            <p className="text-sm text-red-300">La session de paiement a expiré. Veuillez réessayer.</p>
          </div>
        )}

        {paymentStatus === 'changed' && (
          <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-xl px-5 py-3 mb-6" data-testid="plan-changed-banner">
            <Check size={18} className="text-green-400" />
            <p className="text-sm text-white">Votre offre a été changée avec succès.</p>
          </div>
        )}

        {/* User info */}
        <div className="bg-[#1a0e2e] rounded-xl p-6 border border-white/5 mb-8" data-testid="account-info">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#E9641A]/20 flex items-center justify-center text-[#E9641A] font-bold text-xl">
              {(user?.name || user?.email || 'U')[0]?.toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{user?.name || user?.first_name || 'Utilisateur'}</h2>
              <p className="text-sm text-white/40">{user?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <User size={12} className="text-white/25" />
                <span className="text-xs text-white/30">Abonnement actuel :</span>
                <span className="text-xs text-[#E9641A] font-semibold">{plans.find(p => p.id === currentPlan)?.name || currentPlan}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Abonnement en cours */}
        {subInfo && subInfo.plan_id && subInfo.status !== 'none' && (
          <div className="bg-[#1a0e2e] rounded-xl p-6 border border-white/5 mb-8" data-testid="subscription-section">
            <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
              <Crown size={18} className="text-[#E9641A]" /> Mon abonnement
            </h2>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-lg font-bold text-white" data-testid="subscription-plan-name">{subInfo.plan_name || subInfo.plan_id}</p>
                  <span className="text-sm text-white/50">{String(subInfo.price).replace('.', ',')} €/{['annual', 'yearly'].includes(subInfo.billing) ? 'an' : 'mois'}</span>
                  {['active', 'trialing'].includes(subInfo.status) && !subInfo.cancel_at_period_end && (
                    <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30" data-testid="subscription-status-badge">ACTIF</span>
                  )}
                  {subInfo.cancel_at_period_end && (
                    <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30" data-testid="subscription-status-badge">RÉSILIATION PROGRAMMÉE</span>
                  )}
                  {subInfo.status === 'past_due' && (
                    <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30" data-testid="subscription-status-badge">PAIEMENT EN ÉCHEC</span>
                  )}
                  {subInfo.status === 'canceled' && (
                    <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-white/10 text-white/50 border border-white/15" data-testid="subscription-status-badge">RÉSILIÉ</span>
                  )}
                </div>
                {subInfo.current_period_end && (
                  <p className="text-sm text-white/50 mt-1.5" data-testid="subscription-renewal-date">
                    {subInfo.cancel_at_period_end
                      ? `Accès jusqu'au ${new Date(subInfo.current_period_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
                      : `Prochain renouvellement le ${new Date(subInfo.current_period_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                  </p>
                )}
              </div>
              {subInfo.has_stripe_subscription && (
                <div className="flex items-center gap-3 flex-none">
                  {subInfo.cancel_at_period_end ? (
                    <button onClick={handleReactivate} disabled={subBusy} data-testid="subscription-reactivate-btn"
                      className="px-4 py-2.5 text-sm font-semibold text-white bg-[#E9641A] hover:bg-[#FE975E] rounded-lg transition-colors disabled:opacity-50">
                      {subBusy ? <Loader2 size={14} className="animate-spin" /> : "Reprendre l'abonnement"}
                    </button>
                  ) : ['active', 'trialing'].includes(subInfo.status) && (
                    <button onClick={handleCancelSub} disabled={subBusy} data-testid="subscription-cancel-btn"
                      className="px-4 py-2.5 text-sm font-semibold text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg transition-colors disabled:opacity-50">
                      {subBusy ? <Loader2 size={14} className="animate-spin" /> : "Résilier l'abonnement"}
                    </button>
                  )}
                  <button onClick={() => document.querySelector('[data-testid="plans-grid"]')?.scrollIntoView({ behavior: 'smooth' })} data-testid="subscription-change-btn"
                    className="px-4 py-2.5 text-sm font-semibold text-white bg-white/10 hover:bg-white/15 border border-white/10 rounded-lg transition-colors">
                    Changer d'offre
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Connected TV devices */}
        <TvDevicesSection />
        <MarketingPreferences />

        {/* Plans */}
        <h2 className="text-xl font-bold text-white mb-5 flex items-center gap-2">
          <Crown size={20} className="text-[#E9641A]" />
          Choisir un abonnement
        </h2>

        {/* Promo code input */}
        <div className="bg-[#1a0e2e] rounded-xl p-4 border border-white/5 mb-5" data-testid="promo-section">
          <div className="flex items-center gap-3">
            <Ticket size={16} className="text-[#E9641A] flex-none" />
            <div className="flex-1 flex items-center gap-2">
              <input
                value={promoCode}
                onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoValid(null); setPromoError(''); }}
                placeholder="Code promo"
                data-testid="promo-input"
                className="bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-white text-sm font-mono uppercase w-48 focus:outline-none focus:border-[#E9641A]"
              />
              <button onClick={() => validatePromo('')} data-testid="promo-validate-btn" className="px-3 py-2 text-xs bg-white/10 hover:bg-white/15 text-white border border-white/10 rounded-lg transition-colors">Appliquer</button>
              {promoCode && <button onClick={() => { setPromoCode(''); setPromoValid(null); setPromoError(''); }} className="text-white/30 hover:text-white"><X size={14} /></button>}
            </div>
          </div>
          {promoValid && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <Check size={14} className="text-green-400" />
              <span className="text-green-400 font-semibold">{promoValid.code}</span>
              <span className="text-white/40">—</span>
              <span className="text-white/60">{promoValid.discount_type === 'percentage' ? `${promoValid.discount_value}% de réduction` : `${promoValid.discount_value}€ de réduction`}</span>
            </div>
          )}
          {promoError && <p className="mt-2 text-xs text-red-400">{promoError}</p>}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-[3px] border-[#E9641A]/30 border-t-[#E9641A] rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="plans-grid">
            {plans.map(p => {
              const isCurrent = currentPlan === p.id;
              const isAnnual = p.billing === 'annual';
              const isFree = p.price === 0;

              // Calculate discounted price
              let discountedPrice = null;
              if (promoValid && !isFree) {
                if (promoValid.discount_type === 'percentage') {
                  discountedPrice = Math.max(0.5, p.price - (p.price * promoValid.discount_value / 100));
                } else {
                  discountedPrice = Math.max(0.5, p.price - promoValid.discount_value);
                }
                discountedPrice = Math.round(discountedPrice * 100) / 100;
              }

              return (
                <div key={p.id} data-testid={`user-plan-${p.id}`}
                  className={`relative rounded-xl p-5 border transition-all ${
                    isAnnual ? 'border-[#E9641A] ring-1 ring-[#E9641A]/30 bg-[#E9641A]/5' :
                    isCurrent ? 'border-[#229FA1] bg-[#229FA1]/5' :
                    'border-white/8 bg-[#1a0e2e] hover:border-white/15'
                  }`}>
                  {isAnnual && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#E9641A] text-white text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap">2 MOIS OFFERTS</span>
                  )}
                  {isCurrent && (
                    <span className="absolute -top-2.5 right-3 bg-[#229FA1] text-white text-[10px] font-bold px-3 py-0.5 rounded-full">ACTUEL</span>
                  )}
                  <h3 className="text-lg font-bold text-white mt-1">{p.name}</h3>
                  {discountedPrice !== null ? (
                    <div className="my-3">
                      <p className="text-sm text-white/30 line-through">{formatPrice(p)}</p>
                      <p className="text-3xl font-bold text-green-400">{discountedPrice}{p.billing === 'annual' ? '\u20AC/an' : '\u20AC/mois'}</p>
                    </div>
                  ) : (
                    <p className="text-3xl font-bold text-[#E9641A] my-3">{formatPrice(p)}</p>
                  )}
                  <ul className="space-y-2 mb-5">
                    {p.features?.map((f, i) => (
                      <li key={i} className="text-sm text-white/60 flex items-start gap-2">
                        <Check size={14} className="text-green-400 mt-0.5 flex-none" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {isFree ? (
                    isCurrent ? (
                      <div className="w-full py-2 text-center text-xs text-white/30 border border-white/10 rounded-lg">Plan actuel</div>
                    ) : null
                  ) : isCurrent ? (
                    <div className="w-full py-2.5 text-center text-sm text-[#229FA1] font-semibold border border-[#229FA1]/30 rounded-lg bg-[#229FA1]/10">
                      <Check size={14} className="inline mr-1" />Abonnement actif
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSubscribe(p.id)}
                      disabled={checkoutLoading === p.id}
                      data-testid={`subscribe-btn-${p.id}`}
                      className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
                        isAnnual
                          ? 'bg-[#E9641A] hover:bg-[#FE975E] text-white'
                          : 'bg-white/10 hover:bg-white/15 text-white border border-white/10'
                      }`}>
                      {checkoutLoading === p.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : hasActiveSub ? (
                        <><CreditCard size={14} /> Changer pour ce forfait</>
                      ) : (
                        <><CreditCard size={14} /> S'abonner</>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-white/20 mt-6 text-center">Paiement sécurisé par Stripe. Sans engagement, résiliable à tout moment.</p>
      </div>
    </div>
  );
}
